import { useEffect, useMemo, useRef, useState } from "react";
import { Add, FileDownloadOutlined, RestartAlt } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import CheckboxField from "components/CheckboxField";
import localStorageUtil from "utils/localStorageUtil";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_btn_bin.svg";
import { ReactComponent as BinHoverIcon } from "assets/images/icons/icn_btn_bin_hover.svg";
import {
  MSP_DAILY_CREDITS,
  UNITY_PRO_ACCESS_POINT_DAILY_CREDITS,
  UNITY_PRO_SWITCH_DAILY_CREDITS,
} from "const/license";

const createSite = () => ({
  id: uuidv4(),
  name: "New Site",
  accessPoints: 0,
  switches: 0,
  nmsPro: false,
});
const createDefaultSites = () => [{ ...createSite(), name: "Site A" }];
const initialOrganizations = [
  { id: uuidv4(), name: "Organization #1", sites: createDefaultSites() },
];
const STORAGE_KEY = "msp_usage_emulator_config";
const DEFAULT_UNITY_MSP_PRO_ENABLED = true;
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "20px",
    height: "34px",
    "& fieldset": { borderColor: "#172664" },
  },
  "& .MuiOutlinedInput-input": { fontSize: "13px", py: 0 },
};
const createOrganizationsFromSummary = (managedOrganizations) => {
  const organizations = (managedOrganizations || []).map((organization) => ({
    id: organization.orgId,
    name: organization.orgName,
    sites: (organization.sites || []).map((site) => ({
      id: site.id,
      name: site.name,
      accessPoints: Number(site.accessPoints) || 0,
      switches: Number(site.switches) || 0,
      nmsPro: site.services?.unityPro === true,
    })),
  }));

  return organizations.length ? organizations : initialOrganizations;
};
const TrashIconButton = ({ onClick, size = "20px", sx = {} }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <IconButton
      size='small'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      sx={{ p: 0.5, ...sx }}
    >
      {isHovered ? (
        <BinHoverIcon style={{ width: size, height: size }} />
      ) : (
        <BinIcon style={{ width: size, height: size }} />
      )}
    </IconButton>
  );
};

const UsageEmulator = ({ dashboard, managedOrganizations }) => {
  const theme = useTheme();
  const [organizations, setOrganizations] = useState(initialOrganizations);
  const [unityMspProEnabled, setUnityMspProEnabled] = useState(
    DEFAULT_UNITY_MSP_PRO_ENABLED,
  );
  const hasStoredConfigurationRef = useRef(false);
  const hasInitializedFromApiRef = useRef(false);

  useEffect(() => {
    const saved = localStorageUtil.getItem(STORAGE_KEY);
    // Keep configurations saved before the MSP plan toggle was added usable.
    if (Array.isArray(saved) && saved.length) {
      setOrganizations(saved);
      hasStoredConfigurationRef.current = true;
      return;
    }

    if (Array.isArray(saved?.organizations) && saved.organizations.length) {
      setOrganizations(saved.organizations);
      setUnityMspProEnabled(
        saved.unityMspProEnabled ?? DEFAULT_UNITY_MSP_PRO_ENABLED,
      );
      hasStoredConfigurationRef.current = true;
    }
  }, []);
  useEffect(() => {
    if (
      hasStoredConfigurationRef.current ||
      hasInitializedFromApiRef.current ||
      !managedOrganizations?.length
    ) return;

    setOrganizations(createOrganizationsFromSummary(managedOrganizations));
    hasInitializedFromApiRef.current = true;
  }, [managedOrganizations]);
  const dailyUsage = useMemo(() => {
    const proSites = organizations
      .flatMap((organization) => organization.sites)
      .filter((site) => site.nmsPro);

    const proUsage = proSites.reduce(
      (total, site) =>
        total +
        Number(site.accessPoints) * UNITY_PRO_ACCESS_POINT_DAILY_CREDITS +
        Number(site.switches) * UNITY_PRO_SWITCH_DAILY_CREDITS,
      0,
    );
    return (unityMspProEnabled ? MSP_DAILY_CREDITS : 0) + proUsage;
  }, [organizations, unityMspProEnabled]);

  const updateOrganization = (organizationId, update) =>
    setOrganizations((items) =>
      items.map((item) =>
        item.id === organizationId ? { ...item, ...update } : item,
      ),
    );
  const updateSite = (organizationId, siteId, update) =>
    setOrganizations((items) =>
      items.map((organization) =>
        organization.id === organizationId
          ? {
              ...organization,
              sites: organization.sites.map((site) =>
                site.id === siteId ? { ...site, ...update } : site,
              ),
            }
          : organization,
      ),
    );
  const addOrganization = () =>
    setOrganizations((items) => [
      ...items,
      {
        id: uuidv4(),
        name: `Organization #${items.length + 1}`,
        sites: createDefaultSites(),
      },
    ]);
  const addSite = (organizationId) =>
    updateOrganization(organizationId, {
      sites: [
        ...organizations.find((item) => item.id === organizationId).sites,
        createSite(),
      ],
    });
  const reset = () => {
    setOrganizations(createOrganizationsFromSummary(managedOrganizations));
    setUnityMspProEnabled(DEFAULT_UNITY_MSP_PRO_ENABLED);
  };
  const save = () =>
    localStorageUtil.setItem(STORAGE_KEY, {
      organizations,
      unityMspProEnabled,
    });
  const exportJson = () => {
    const link = document.createElement("a");
    link.href = `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify({ organizations, unityMspProEnabled }, null, 2))}`;
    link.download = "msp-usage-emulator.json";
    link.click();
  };
  const importJson = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (Array.isArray(data)) {
          setOrganizations(data);
          setUnityMspProEnabled(DEFAULT_UNITY_MSP_PRO_ENABLED);
        } else if (Array.isArray(data?.organizations)) {
          setOrganizations(data.organizations);
          setUnityMspProEnabled(
            data.unityMspProEnabled ?? DEFAULT_UNITY_MSP_PRO_ENABLED,
          );
        }
      } catch {
        /* ignore invalid JSON */
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          minHeight: 64,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          mb: 1,
        }}
      >
        <Typography
          variant='h4'
          sx={{ color: "#172664", fontSize: 22, fontWeight: "bold" }}
        >
          Usage Emulator
        </Typography>
        <Stack direction='row' spacing={2}>
          <OutlineButton
            startIcon={<RestartAlt />}
            onClick={reset}
            height='34px'
          >
            Reset
          </OutlineButton>
          <OutlineButton
            startIcon={<FileDownloadOutlined />}
            onClick={exportJson}
            height='34px'
          >
            Export
          </OutlineButton>
          <OutlineButton component='label' height='34px'>
            Import
            <input
              hidden
              type='file'
              accept='application/json,.json'
              onChange={importJson}
            />
          </OutlineButton>
          <SolidButton onClick={save} height='34px'>
            Save
          </SolidButton>
        </Stack>
      </Stack>
      <Paper
        variant='outlined'
        sx={{ p: 3, borderRadius: "20px", borderColor: "divider" }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={8}>
          {[
            ["Current Balance", Number(dashboard?.balance) || 0, "Credits"],
            ["Daily Usage", dailyUsage, "Credits"],
            ["Month Usage (30 Days)", dailyUsage * 30, "Credits"],
            ["Year Usage (365 Days)", dailyUsage * 365, "Credits"],
          ].map(([label, value, unit]) => (
            <Box key={label}>
              <Typography
                variant='body2'
                fontWeight='bold'
                sx={{ color: "#172664" }}
              >
                {label}
              </Typography>
              <Typography
                sx={{ color: "#008514", fontSize: 42, fontWeight: "bold" }}
              >
                {Number(value).toLocaleString()}
              </Typography>
              <Typography variant='caption'>{unit}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 0 }}
      >
        <Box>
          <Typography fontWeight='bold' sx={{ color: "#172664", fontSize: 18 }}>
            Configuration
          </Typography>
          <Typography variant='caption'>
            Edits below update the simulated results above.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <CheckboxField
              label={`Unity MSP Pro (${MSP_DAILY_CREDITS} Credits/day)`}
              checked={unityMspProEnabled}
              onChange={setUnityMspProEnabled}
            />
          </Box>
        </Box>
      </Stack>
      {organizations.map((organization, organizationIndex) => (
        <Paper
          key={organization.id}
          variant='outlined'
          sx={{
            p: 2,
            mt: organizationIndex === 0 ? "8px !important" : undefined,
            borderRadius: "20px",
            borderColor: "divider",
          }}
        >
          <Stack spacing={2}>
            <Stack direction='row' justifyContent='space-between'>
              <TextField
                size='small'
                sx={inputSx}
                value={organization.name}
                onChange={(event) =>
                  updateOrganization(organization.id, {
                    name: event.target.value,
                  })
                }
              />
              <OutlineButton
                startIcon={<Add />}
                onClick={() => addSite(organization.id)}
                height='34px'
              >
                Add Site
              </OutlineButton>
            </Stack>
            <TableContainer>
              <Table size='small' sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        verticalAlign: "top",
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: "#172664",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        pb: 2,
                      },
                    }}
                  >
                    <TableCell sx={{ width: "30%" }}>Site</TableCell>
                    <TableCell align='center' sx={{ width: "18%" }}>
                      Access Points
                    </TableCell>
                    <TableCell align='center' sx={{ width: "18%" }}>
                      Switches
                    </TableCell>
                    <TableCell align='center' sx={{ width: "20%" }}>
                      Nuclias Unity Pro
                    </TableCell>
                    <TableCell align='right' sx={{ width: "14%" }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organization.sites.map((site, index) => (
                    <TableRow
                      key={site.id}
                      sx={{
                        bgcolor: index % 2 === 1 ? "grey.50" : "transparent",
                        "& td": { borderBottom: "none", py: 1.5 },
                      }}
                    >
                      <TableCell>
                        <TextField
                          size='small'
                          fullWidth
                          sx={inputSx}
                          value={site.name}
                          onChange={(event) =>
                            updateSite(organization.id, site.id, {
                              name: event.target.value,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <TextField
                          size='small'
                          type='text'
                          sx={{ ...inputSx, width: 120 }}
                          value={site.accessPoints}
                          onChange={(event) => {
                            const value = event.target.value.replace(/\D/g, "");
                            if (value === "" || Number(value) <= 100000)
                              updateSite(organization.id, site.id, {
                                accessPoints: value,
                              });
                          }}
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <TextField
                          size='small'
                          type='text'
                          sx={{ ...inputSx, width: 120 }}
                          value={site.switches}
                          onChange={(event) => {
                            const value = event.target.value.replace(/\D/g, "");
                            if (value === "" || Number(value) <= 100000)
                              updateSite(organization.id, site.id, {
                                switches: value,
                              });
                          }}
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <CheckboxField
                            checked={site.nmsPro}
                            onChange={(checked) =>
                              updateSite(organization.id, site.id, {
                                nmsPro: checked,
                              })
                            }
                          />
                        </Box>
                      </TableCell>
                      <TableCell align='right'>
                        <TrashIconButton
                          onClick={() =>
                            updateOrganization(organization.id, {
                              sites: organization.sites.filter(
                                (item) => item.id !== site.id,
                              ),
                            })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      ))}
      <OutlineButton
        startIcon={<Add />}
        onClick={addOrganization}
        height='34px'
        sx={{ alignSelf: "flex-start" }}
      >
        Add Organization
      </OutlineButton>
    </Stack>
  );
};

export default UsageEmulator;
